import React from "react";
import { MenuItem, TextField } from "@mui/material";
import { useIntl } from "react-intl";

// Linking a single-choice question to one of its project's segments
// (Question.segment on the BE). A linked question's choices ARE the segment's
// choices: the BE rewrites them on every save and whenever the segment changes,
// so the builder shows them locked.

// The choices a question takes when linked to `segment`, in the shape the
// builder keeps: label + the segment choice it stands for.
export const segmentChoices = (segment) =>
    (segment?.choices || []).map((c) => ({ text: c.label, segmentChoice: c.id }));

// "Linked segment: None | Region | Team ...". A segment another question of the
// assessment already uses is offered disabled (one question per segment), and
// so is one with no choices yet. Renders nothing while the project has no
// segments and the question isn't linked.
export function SegmentLinkSelect({ id, segments, value, onChange, usedSegmentIds = [], disabled = false, sx }) {
    const intl = useIntl();
    if (!segments.length && !value) return null;
    const current = value != null ? String(value) : "";
    return (
        <TextField
            select
            size="small"
            id={id}
            label={intl.formatMessage({ id: "segment_link.label", defaultMessage: "Linked segment" })}
            value={current}
            disabled={disabled}
            onChange={(e) => {
                const next = e.target.value;
                onChange(next ? segments.find((s) => String(s.id) === next) || null : null);
            }}
            helperText={
                current
                    ? intl.formatMessage({
                          id: "segment_link.locked_choices",
                          defaultMessage: "The choices come from the segment and update with it.",
                      })
                    : intl.formatMessage({
                          id: "segment_link.help",
                          defaultMessage: "Optional. Link this question to one of the project's segments.",
                      })
            }
            sx={{ minWidth: 240, ...sx }}
            inputProps={{ "data-testid": `${id}-input` }}
        >
            <MenuItem value="">
                {intl.formatMessage({ id: "segment_link.none", defaultMessage: "Not linked" })}
            </MenuItem>
            {segments.map((s) => {
                const used = String(s.id) !== current && usedSegmentIds.map(String).includes(String(s.id));
                const empty = !(s.choices || []).length;
                return (
                    <MenuItem key={s.id} value={String(s.id)} disabled={used || empty}>
                        {s.title}
                        {used
                            ? ` — ${intl.formatMessage({ id: "segment_link.used", defaultMessage: "already linked" })}`
                            : empty
                              ? ` — ${intl.formatMessage({ id: "segment_link.empty", defaultMessage: "no choices yet" })}`
                              : ""}
                    </MenuItem>
                );
            })}
            {/* Linked to a segment that is no longer listed (another project's,
                or one being deleted): still shown, so the value is readable. */}
            {current && !segments.some((s) => String(s.id) === current) && (
                <MenuItem value={current} disabled>
                    {intl.formatMessage({ id: "segment_link.unknown", defaultMessage: "Unavailable segment" })}
                </MenuItem>
            )}
        </TextField>
    );
}
